import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-runtime";
import { AssignProjectToAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/assignProjectToApiKeyRPC";
import { DeleteAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/deleteApiKeyRPC";
import { GenerateNewAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/generateNewApiKeyRPC";
import { GetActiveProjectsForAPIKeyManagementRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/getActiveProjectsForApiKeyManagementRPC";
import { GetProjectsByAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/getProjectsByApiKeyRPC";
import { GetPublicAPIKeysRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/getPublicApiKeysRPC";
import { InvalidateAndRotateAPIKeyRPC } from "@pulse/pulse-rpcs/lib/external-public-apis/invalidateAndRotateApiKeyRPC";
import { getAPIClient } from "../../networking/APIClient";
import { APIKeyModel, createAPIKeyModel } from "../models/APIKeyModel";
import {
  APIKeyProjectModel,
  createAPIKeyProjectModel,
} from "../models/APIKeyProjectModel";
import {
  assignProjectToAPIKeyRPCClient,
  deleteAPIKeyRPCClient,
  generateNewAPIKeyRPCClient,
  getActiveProjectsForAPIKeyManagementRPCClient,
  getProjectsByAPIKeyRPCClient,
  getPublicAPIKeysRPCClient,
  invalidateAndRotateAPIKeyRPCClient,
} from "../rpcs/RPC";

export const API_KEYS_PER_PAGE = 10;

export interface OneTimeAPICredentials {
  apiKeyId: string;
  apiSecretKey: string;
  expiresAt: string;
}

const getInitialState = (): SnapshotIn<typeof APIKeyManagementStore> => ({
  apiKeys: [],
  activeProjects: [],
  totalItems: 0,
  currentPageIndex: 1,
  loadingAPIKeys: true,
  loadingProjects: true,
  loadingAction: false,
  rpcError: null,
});

export const APIKeyManagementStore = types
  .model("APIKeyManagementStore", {
    apiKeys: types.array(APIKeyModel),
    activeProjects: types.array(APIKeyProjectModel),
    totalItems: types.integer,
    currentPageIndex: types.integer,
    loadingAPIKeys: types.boolean,
    loadingProjects: types.boolean,
    loadingAction: types.boolean,
    rpcError: types.maybeNull(types.string),
  })
  .actions((store) => ({
    clearRPCError: (): void => {
      store.rpcError = null;
    },
    getAPIKeys: flow(function* (pageIndex = 1, loadingOptionsChange = false) {
      if (!loadingOptionsChange) store.loadingAPIKeys = true;
      try {
        const result: LeoRPCResult<
          GetPublicAPIKeysRPC.Response,
          GetPublicAPIKeysRPC.Errors.Errors
        > = yield getPublicAPIKeysRPCClient(getAPIClient(store)).execute(
          new GetPublicAPIKeysRPC.Request(pageIndex - 1, API_KEYS_PER_PAGE),
        );
        if ("response" in result) {
          store.currentPageIndex = pageIndex;
          store.totalItems = result.response.totalItems;
          store.apiKeys.replace(
            result.response.apiKeys.map((apiKey) =>
              createAPIKeyModel({
                apiKeyId: apiKey.apiKeyId.uuid,
                status: apiKey.status,
                expiresAt: apiKey.expiresAt.timestamp,
                isAdmin: apiKey.isAdmin,
                createdAt: apiKey.createdAt.timestamp,
                projectIds: apiKey.projectIds.map(({ uuid }) => uuid),
              }),
            ),
          );
        }
      } finally {
        if (!loadingOptionsChange) store.loadingAPIKeys = false;
      }
    }),
    getActiveProjects: flow(function* () {
      store.loadingProjects = true;
      try {
        const result: LeoRPCResult<
          GetActiveProjectsForAPIKeyManagementRPC.Response,
          GetActiveProjectsForAPIKeyManagementRPC.Errors.Errors
        > = yield getActiveProjectsForAPIKeyManagementRPCClient(
          getAPIClient(store),
        ).execute(new GetActiveProjectsForAPIKeyManagementRPC.Request());
        if ("response" in result) {
          store.activeProjects.replace(
            result.response.projects.map((project) =>
              createAPIKeyProjectModel({
                projectId: project.projectId.uuid,
                projectName: project.projectName,
              }),
            ),
          );
        }
      } finally {
        store.loadingProjects = false;
      }
    }),
    getProjectsByAPIKey: flow(function* (apiKeyId: string) {
      store.loadingAction = true;
      store.rpcError = null;
      try {
        const result: LeoRPCResult<
          GetProjectsByAPIKeyRPC.Response,
          GetProjectsByAPIKeyRPC.Errors.Errors
        > = yield getProjectsByAPIKeyRPCClient(getAPIClient(store)).execute(
          new GetProjectsByAPIKeyRPC.Request(new LeoUUID(apiKeyId)),
        );
        if ("response" in result) {
          return result.response.projects.map(({ uuid }) => uuid);
        }
        store.rpcError = result.error.code;
        return null;
      } finally {
        store.loadingAction = false;
      }
    }),
    generateAPIKey: flow(function* (projectIds: string[]) {
      store.loadingAction = true;
      store.rpcError = null;
      try {
        const result: LeoRPCResult<
          GenerateNewAPIKeyRPC.Response,
          GenerateNewAPIKeyRPC.Errors.Errors
        > = yield generateNewAPIKeyRPCClient(getAPIClient(store)).execute(
          new GenerateNewAPIKeyRPC.Request(
            projectIds.map((projectId) => new LeoUUID(projectId)),
          ),
        );
        if ("response" in result) {
          return {
            apiKeyId: result.response.newAPIKey.apiKeyId.uuid,
            apiSecretKey: result.response.newAPIKey.apiSecretKey,
            expiresAt: result.response.expiresAt.timestamp,
          } satisfies OneTimeAPICredentials;
        }
        store.rpcError = result.error.code;
        return null;
      } finally {
        store.loadingAction = false;
      }
    }),
    updateProjectAssignments: flow(function* (
      apiKeyId: string,
      projectIds: string[],
    ) {
      store.loadingAction = true;
      store.rpcError = null;
      try {
        const result: LeoRPCResult<
          AssignProjectToAPIKeyRPC.Response,
          AssignProjectToAPIKeyRPC.Errors.Errors
        > = yield assignProjectToAPIKeyRPCClient(getAPIClient(store)).execute(
          new AssignProjectToAPIKeyRPC.Request(
            new LeoUUID(apiKeyId),
            projectIds.map((projectId) => new LeoUUID(projectId)),
          ),
        );
        if ("error" in result) store.rpcError = result.error.code;
        return "response" in result;
      } finally {
        store.loadingAction = false;
      }
    }),
    rotateAPIKey: flow(function* (apiKeyId: string) {
      store.loadingAction = true;
      store.rpcError = null;
      try {
        const result: LeoRPCResult<
          InvalidateAndRotateAPIKeyRPC.Response,
          InvalidateAndRotateAPIKeyRPC.Errors.Errors
        > = yield invalidateAndRotateAPIKeyRPCClient(
          getAPIClient(store),
        ).execute(
          new InvalidateAndRotateAPIKeyRPC.Request(new LeoUUID(apiKeyId)),
        );
        if ("response" in result) {
          return {
            apiKeyId: result.response.newAPIKey.apiKeyId.uuid,
            apiSecretKey: result.response.newAPIKey.apiSecretKey,
            expiresAt: result.response.expiresAt.timestamp,
          } satisfies OneTimeAPICredentials;
        }
        store.rpcError = result.error.code;
        return null;
      } finally {
        store.loadingAction = false;
      }
    }),
    deleteAPIKey: flow(function* (apiKeyId: string) {
      store.loadingAction = true;
      store.rpcError = null;
      try {
        const result: LeoRPCResult<
          DeleteAPIKeyRPC.Response,
          DeleteAPIKeyRPC.Errors.Errors
        > = yield deleteAPIKeyRPCClient(getAPIClient(store)).execute(
          new DeleteAPIKeyRPC.Request(new LeoUUID(apiKeyId)),
        );
        if ("error" in result) store.rpcError = result.error.code;
        return "response" in result;
      } finally {
        store.loadingAction = false;
      }
    }),
  }))
  .actions((store) => ({
    refreshCurrentPage: flow(function* () {
      yield store.getAPIKeys(store.currentPageIndex);
    }),
    refreshCurrentPageAfterDeletion: flow(function* () {
      yield store.getAPIKeys(store.currentPageIndex);
      if (
        store.currentPageIndex > 1 &&
        store.apiKeys.length === 0 &&
        store.totalItems > 0
      ) {
        yield store.getAPIKeys(store.currentPageIndex - 1);
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createAPIKeyManagementStore = (): Instance<
  typeof APIKeyManagementStore
> => APIKeyManagementStore.create(getInitialState());
